import { Anchor } from '@components/Anchor';
import { LinkText } from '@components/LinkText';
import { DataTable } from '@components/Table/DataTable';
import { Pagination } from '@components/Table/Pagination';
import { TableCellAction } from '@components/Table/TableCellAction';
import { TableHeaderCellAction } from '@components/Table/TableHeaderCellAction';
import { TableKeyCell } from '@components/Table/TableKeyCell';
import { TableSortCell } from '@components/Table/TableSortCell';
import { TableSortRow } from '@components/Table/TableSortRow';
import { useCellPopover } from '@hooks/useCellPopover';
import { IData } from '@hooks/useData';
import useFormatter from '@hooks/useFormatter';
import { IMenu } from '@hooks/useMenu';
import { IModalWindow } from '@hooks/useModalWindow';
import { TableBody, TableCell, TableContainerProps, TableHead, TableRow } from '@material-ui/core';
import IFatura, { useFaturaTipoColor } from '@model/Fatura';
import { useFaturaStatusColor } from '@model/FaturaStatus';
import { IFaturaFilter } from '@services/FaturaService';
import React from 'react';
import { FaturaCanceladaPopover } from './FaturaCanceladaPopover';
import { FaturaDetalheModal } from './FaturaDetalheModal';

export type FaturaTableProps = {
    isLoading: boolean
    data: IData<IFatura, IFaturaFilter>
    faturaDetalheModal: IModalWindow
    tableContainerProps?: TableContainerProps
    menu: IMenu
}

export function FaturaTable ({ isLoading, data, faturaDetalheModal, tableContainerProps, menu }: FaturaTableProps) {
    const formatter = useFormatter();
    const faturaStatusColor = useFaturaStatusColor();
    const faturaTipoColor = useFaturaTipoColor();
    const cellPopover = useCellPopover<IFatura>();

    return (
        <React.Fragment>
            <DataTable 
                isLoading={isLoading} 
                itemCount={data.itemsCount}
                tableContainerProps={tableContainerProps}
            >
                <TableHead>
                    <Pagination pagination={data.pagination} />

                    <TableSortRow sort={data.sort}>
                        <TableSortCell fieldName="faturaId">ID</TableSortCell>
                        <TableSortCell fieldName="clienteNome">Cliente</TableSortCell>
                        <TableSortCell fieldName="empresaNome">Empresa</TableSortCell>
                        <TableSortCell fieldName="dataVencimento">Vencimento</TableSortCell>
                        <TableSortCell fieldName="valorTotal">Vlr. Total</TableSortCell>
                        <TableSortCell fieldName="valorPago">Vlr. Pago</TableSortCell>
                        <TableSortCell fieldName="saldo">Saldo</TableSortCell>
                        <TableSortCell fieldName="dataPagamento">Pagamento</TableSortCell>
                        <TableSortCell fieldName="formaPagamentoNome">Forma Pgto.</TableSortCell>
                        <TableSortCell fieldName="tipo">Tipo</TableSortCell>
                        <TableSortCell fieldName="faturaStatusNome">Status</TableSortCell>
                        <TableHeaderCellAction />
                    </TableSortRow>
                </TableHead>
                
                <TableBody>
                    {
                        data.items.map((fatura, index) => {
                            return (
                                <TableRow key={index}>
                                    <TableKeyCell><Anchor onClick={data.handleRowFocused(fatura, faturaDetalheModal.open)}>{fatura.faturaId}</Anchor></TableKeyCell>
                                    <TableCell><LinkText to={`/cliente/${fatura.clienteId}`} text={fatura.clienteNome} /></TableCell>
                                    <TableCell>{fatura.empresaNome}</TableCell>
                                    <TableCell>{formatter.toDate(fatura.dataVencimento)}</TableCell>
                                    <TableCell>{formatter.toFloat(fatura.valorTotal)}</TableCell>
                                    <TableCell>{formatter.toFloat(fatura.valorPago)}</TableCell>
                                    <TableCell>{formatter.toFloat(fatura.saldo)}</TableCell>
                                    <TableCell>{formatter.toDate(fatura.dataPagamento)}</TableCell>
                                    <TableCell>{fatura.formaPagamentoNome}</TableCell>
                                    
                                    <TableCell className={faturaTipoColor[fatura.tipo]}>
                                        { fatura.tipo }
                                    </TableCell>

                                    <TableCell 
                                        className={faturaStatusColor[fatura.faturaStatusId]}
                                        onMouseEnter={cellPopover.handleMouseOpen(fatura)}
                                        onMouseLeave={cellPopover.close}
                                    >
                                        { fatura.faturaStatusNome }
                                    </TableCell>

                                    <TableCellAction onButtonClick={data.handleRowFocused(fatura, menu.open)} />
                                </TableRow>
                            )
                        })
                    }
                    <Pagination pagination={data.pagination} />
                </TableBody>
            </DataTable>

            {
                cellPopover.record !== undefined && cellPopover.record.dataHoraCancelamento !== undefined && (
                    <FaturaCanceladaPopover cellPopover={cellPopover} fatura={cellPopover.record} />
                )
            }

            {
                data.focusedRow !== undefined && (
                    <FaturaDetalheModal 
                        faturaId={data.focusedRow.faturaId}
                        modal={faturaDetalheModal}
                    />
                )
            }
        </React.Fragment>
    )
}